import { useMemo, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { activeLanguage } from '../i18n/language';
import {
  countryLocale,
  countryName,
  countryOptions,
  searchCountries,
  type CountryChoice,
} from '../lib/countries';
import { colors } from '../styles/theme';

const DEFAULT_INPUT_STYLE: CSSProperties = {
  background: colors.canvas,
  border: `1.5px solid ${colors.border}`,
  color: colors.ink,
};

type Props = Readonly<
  CountryChoice & {
    id: string;
    /** The stored ISO 3166-1 alpha-2 code, or null. */
    value: string | null;
    onChange: (code: string | null) => void;
    onBlur?: () => void;
    disabled?: boolean;
    placeholder?: string;
    inputStyle?: CSSProperties;
  }
>;

/**
 * The one country picker of the portal: a searchable dropdown that lists Mexico, the United
 * States and Canada first and then every other country by name in the reader's language. A person
 * types a name or an ISO code to narrow it; the list never shows codes, and the value it hands
 * back is the code. `codes` restricts it to a field's own set.
 */
export default function CountryPicker({
  id,
  value,
  onChange,
  onBlur,
  disabled = false,
  placeholder = '— Select —',
  inputStyle = DEFAULT_INPUT_STYLE,
  codes,
  labels,
}: Props): JSX.Element {
  const locale = countryLocale(activeLanguage());
  const options = useMemo(() => countryOptions(locale, { codes, labels }), [locale, codes, labels]);
  // The text being typed; null while the person is not searching.
  const [query, setQuery] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  // The person deleted what was there on purpose, as opposed to only opening the list.
  const [erased, setErased] = useState(false);

  const open = query !== null;
  const shown = useMemo(() => searchCountries(options, query ?? ''), [options, query]);
  const selected = value ? options.find((o) => o.code === value)?.name ?? countryName(value, locale) : null;
  const listId = `${id}-countries`;

  const choose = (code: string): void => {
    onChange(code);
    setErased(false);
    setQuery(null);
  };

  const close = (): void => {
    // Emptying the box and leaving it clears the field; opening the list and leaving does not.
    if (erased && query === '' && value !== null) onChange(null);
    setErased(false);
    setQuery(null);
    onBlur?.();
  };

  const onKey = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setQuery('');
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setHighlighted((current) => Math.max(0, Math.min(shown.length - 1, current + step)));
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      const pick = shown[highlighted];
      if (pick) choose(pick.code);
    } else if (event.key === 'Escape' && open) {
      // Escape closes the list only, never the dialog the picker sits in.
      event.preventDefault();
      event.stopPropagation();
      setQuery(null);
    }
  };

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        translate="no"
        autoComplete="off"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && shown[highlighted] ? `${id}-${shown[highlighted].code}` : undefined}
        value={open ? query : (selected ?? '')}
        placeholder={open && selected ? selected : placeholder}
        disabled={disabled}
        onFocus={() => setQuery('')}
        onClick={() => {
          if (query === null) setQuery('');
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setErased(event.target.value === '');
          setHighlighted(0);
        }}
        onKeyDown={onKey}
        onBlur={close}
        className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
        style={inputStyle}
      />
      {open && shown.length === 0 && (
        <p
          id={listId}
          className="absolute left-0 top-full z-20 mt-1 w-full rounded-xl px-3 py-2 text-sm shadow-lg"
          style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
        >
          No country matches
        </p>
      )}
      {open && shown.length > 0 && (
        // A native list box: the highlighted country is its selected option. Focus stays in the
        // input (the list is skipped by Tab), so the keys above drive it.
        <select
          id={listId}
          size={Math.max(2, Math.min(8, shown.length))}
          tabIndex={-1}
          aria-label="Countries"
          value={shown[highlighted]?.code ?? ''}
          onChange={(event) => setHighlighted(shown.findIndex((o) => o.code === event.target.value))}
          className="absolute left-0 top-full z-20 mt-1 w-full rounded-xl py-1 text-sm shadow-lg outline-none"
          style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.ink }}
        >
          {shown.map((option, index) => (
            <option
              key={option.code}
              id={`${id}-${option.code}`}
              value={option.code}
              translate="no"
              // mousedown, not click: the input must not lose focus before the choice lands.
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option.code);
              }}
              onMouseEnter={() => setHighlighted(index)}
              className="cursor-pointer px-3 py-2"
              style={{ fontWeight: option.code === value ? 600 : 400 }}
            >
              {option.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
